import { Injectable } from '@angular/core';
import {
  IbanCountryMeta,
  ibanRegistry,
  PositionLookup,
} from './data/iban-registry';
import { Iban } from './types/iban';

export enum IbanValidity {
  Valid,
  InvalidCheckDigits,
  UnsupportedCountry,
  CountrySpecificInvalidity,
}

export interface BankInfo {
  bankId: string;
  branchId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class IbanService {
  private readonly _ibanBankData: Map<string, IbanCountryMeta> = new Map();

  constructor() {
    this._ibanBankData = new Map(Object.entries(ibanRegistry));
  }

  private getFromPositionLookup(iban: Iban, lookup: PositionLookup): string {
    const { bban } = iban;
    const [start, end] = lookup;

    return bban.substring(start, end + 1);
  }

  /**
   * @param iban The iban
   *
   * @returns
   * The meta information for the country that this iban belongs to.
   * Undefined, if there is no meta information for this country.
   */
  public getIbanCountryMeta(iban: Iban): IbanCountryMeta {
    const { countryCode } = iban;
    return this._ibanBankData.get(countryCode);
  }

  /**
   * @param iban The iban
   *
   * @returns
   * Bank meta information extracted from the given IBAN
   */
  public getBankInfo(iban: Iban): BankInfo {
    const ibanCountryMeta = this.getIbanCountryMeta(iban);
    if (!ibanCountryMeta) {
      return undefined;
    }

    const { bankIdPosition, branchIdPosition } = ibanCountryMeta.bban;
    const bankId = this.getFromPositionLookup(iban, bankIdPosition);
    const branchId = branchIdPosition
      ? this.getFromPositionLookup(iban, branchIdPosition)
      : undefined;

    return {
      bankId,
      branchId,
    };
  }

  /**
   * @param iban The string from which the iban should be created
   * @param format Format of the given iban string. Defaults to `'electronic'`.
   *
   * @returns
   * Instance of Iban
   */
  fromString(
    iban: string,
    format: 'electronic' | 'print' = 'electronic',
  ): Iban {
    return format === 'print' ? Iban.parse(iban) : new Iban(iban);
  }

  /**
   * @param iban The iban to validate
   * @param countries List of allowed countries
   *
   * @returns
   * State describing the IBAN validity
   */
  validate(iban: string, countries?: string[]): IbanValidity {
    if (iban === null) {
      return IbanValidity.InvalidCheckDigits;
    }

    const o = this.fromString(iban);

    // Check if iban country code is included in the given countries
    if (
      countries &&
      !countries.find((c) => c.toUpperCase() === o.countryCode)
    ) {
      return IbanValidity.UnsupportedCountry;
    }

    // Try to perform country specific checks
    const countryMeta = this.getIbanCountryMeta(o);
    if (countryMeta) {
      const { bban, checkDigits } = countryMeta;
      if (bban.structure && !bban.structure.test(o.bban)) {
        return IbanValidity.CountrySpecificInvalidity;
      }

      // If there is a specific checkDigits check, use this instead of Module-97
      if (checkDigits) {
        return checkDigits === o.checkDigits
          ? IbanValidity.Valid
          : IbanValidity.InvalidCheckDigits;
      }
    }

    return o.checkDigitsValid()
      ? IbanValidity.Valid
      : IbanValidity.InvalidCheckDigits;
  }
}
